import _ from 'lodash';

import { getSubstitutionProducts, updateSubstitution } from 'shared/models/api';

const SUBSTITUTION_OPTIONS_LOADING = 'SUBSTITUTION_OPTIONS_LOADING';
const SUBSTITUTION_OPTIONS_ERROR = 'SUBSTITUTION_OPTIONS_ERROR';
const SUBSTITUTION_OPTIONS = 'SUBSTITUTION_OPTIONS';
const UPDATE_SUBSTITUTION_STATUS = 'UPDATE_SUBSTITUTION_STATUS';
const SUBSTITUTION_REMOVE = 'SUBSTITUTION_REMOVE';
const SUBSTITUTION_KEEP = 'SUBSTITUTION_KEEP';
const SUBSTITUTION_CART_ADD = 'SUBSTITUTION_CART_ADD';
const RESET_SUBSTITUTION = 'RESET_SUBSTITUTION';
const SUBSTITUTION_REQUEST_LOADING = 'SUBSTITUTION_REQUEST_LOADING';
const SUBSTITUTION_REQUEST_LOADED = 'SUBSTITUTION_REQUEST_LOADED';
const SUBSTITUTION_REQUEST_ERROR = 'SUBSTITUTION_REQUEST_ERROR';

const init = {
  order: {},
  optionsLoading: false,
  options: {},
  updates: {},
  status: null,
  loadingStates: {},
  errors: {}
};

/*
{
  updates: {
    SKU-ID-00: {
      removed: { sku_id: "SKU-ID-00", quantity: 2, ... },
      kept: { sku_id: "SKU-ID-00", quantity: 1, ... },
      added: { sku_id "SKU-ID-01", quantity: 2, ... }
    }
  }
}
*/

export default function reducer(state = init, action = {}) {
  switch (action.type) {
    case SUBSTITUTION_OPTIONS_LOADING:
      return { ...state, optionsLoading: true };
    case SUBSTITUTION_OPTIONS_ERROR:
      return { ...state, optionsLoading: false };
    case SUBSTITUTION_OPTIONS:
      return {
        ...state,
        optionsLoading: false,
        options: {
          ...state.options,
          [action.sku]: action.options
        }
      };
    case UPDATE_SUBSTITUTION_STATUS:
      return { ...state, status: action.status };
    case SUBSTITUTION_REMOVE:
      return {
        ...state,
        loadingStates: { ...state.loadingStates, [action.item.sku_id]: false },
        updates: removeItem(state.updates, action.item, action.quantity)
      };
    case SUBSTITUTION_KEEP:
      return {
        ...state,
        loadingStates: { ...state.loadingStates, [action.item.sku_id]: false },
        updates: keepItem(state.updates, action.item, action.quantity)
      };
    case SUBSTITUTION_CART_ADD:
      return {
        ...state,
        loadingStates: { ...state.loadingStates, [action.item.sku_id]: false },
        updates: addItem(
          state.updates,
          action.item.sku_id,
          action.newItem,
          action.quantity
        )
      };
    case RESET_SUBSTITUTION:
      return {
        ...state,
        loadingStates: { ...state.loadingStates, [action.item.sku_id]: false },
        updates: _.omit(state.updates, action.item.sku_id)
      };
    case SUBSTITUTION_REQUEST_LOADING:
      return {
        ...state,
        loadingStates: { ...state.loadingStates, [action.id]: true },
        errors: { ...state.errors, [action.id]: null }
      };
    case SUBSTITUTION_REQUEST_LOADED:
      return {
        ...state,
        loadingStates: { ...state.loadingStates, [action.id]: false }
      };
    case SUBSTITUTION_REQUEST_ERROR:
      return {
        ...state,
        loadingStates: { ...state.loadingStates, [action.id]: false },
        errors: { ...state.errors, [action.id]: action.error }
      };
    default:
      return state;
  }
}

function removeItem(updates = {}, item, quantity) {
  const sku = item.sku_id;
  const updatesForItem = updates[sku] || {};

  return {
    ...updates,
    [sku]: {
      ...updatesForItem,
      removed: {
        ...item,
        quantity
      }
    }
  };
}

function keepItem(updates = {}, item, quantity) {
  const sku = item.sku_id;
  const updatesForItem = updates[sku] || {};

  return {
    ...updates,
    [sku]: {
      ...updatesForItem,
      kept: {
        ...item,
        quantity
      }
    }
  };
}

function addItem(updates = {}, sku, newItem, quantity) {
  const updatesForItem = updates[sku] || {};

  return {
    ...updates,
    [sku]: {
      ...updatesForItem,
      added: {
        ...newItem,
        quantity
      }
    }
  };
}

export function getSubstitutionOptions(sku) {
  return dispatch => {
    dispatch({ type: SUBSTITUTION_OPTIONS_LOADING });
    return getSubstitutionProducts(sku)
      .then(options => {
        return dispatch({
          type: SUBSTITUTION_OPTIONS,
          sku,
          options
        });
      })
      .catch(err => {
        dispatch({ type: SUBSTITUTION_OPTIONS_ERROR });
        console.log(err); // eslint-disable-line no-console
      });
  };
}

export function updateStatus(status) {
  return (dispatch, getState) => {
    const state = getState();
    const user = state.login.user;
    const order = state.substitutions.order;

    dispatch({ type: UPDATE_SUBSTITUTION_STATUS, status });
    return updateSubstitution({ user, order, status });
  };
}

export function removeOutOfStockItem(item) {
  return (dispatch, getState) => {
    const state = getState();
    const user = state.login.user;
    const order = state.substitutions.order;

    const status = `Removed ${item.title}`;
    dispatch({ type: UPDATE_SUBSTITUTION_STATUS, status });

    const keepQty = item.stock_level || 0;
    const removeQty = item.quantity - keepQty;

    dispatch({ type: SUBSTITUTION_REQUEST_LOADING, id: item.sku_id });

    return updateSubstitution({
      user,
      order,
      status,
      products: [
        {
          sku: item.sku_id,
          original_sku: item.sku_id,
          quantity: keepQty
        }
      ]
    })
      .then(() => {
        if (keepQty)
          dispatch({ type: SUBSTITUTION_KEEP, item, quantity: keepQty });
        return dispatch({
          type: SUBSTITUTION_REMOVE,
          item,
          quantity: removeQty
        });
      })
      .catch(res => {
        console.log(res); // eslint-disable-line no-console
        const err = res.body && res.body.message;
        return dispatch({
          type: SUBSTITUTION_REQUEST_ERROR,
          id: item.sku_id,
          error: err
        });
      });
  };
}

export function chooseSubstitution(item, newItem, quantity) {
  return (dispatch, getState) => {
    const state = getState();
    const user = state.login.user;
    const order = state.substitutions.order;
    const updates = state.substitutions.updates || {};

    const status = `Replaced ${item.title}`;
    dispatch({ type: UPDATE_SUBSTITUTION_STATUS, status });

    dispatch({ type: SUBSTITUTION_REQUEST_LOADING, id: item.sku_id });

    const keepQty = item.stock_level || 0;
    const removeQty = item.quantity - keepQty;

    let products = [
      {
        sku: newItem.sku_id,
        original_sku: item.sku_id,
        quantity
      },
      {
        sku: item.sku_id,
        original_sku: item.sku_id,
        quantity: keepQty
      }
    ];

    const prevAdded = updates[item.sku_id] && updates[item.sku_id].added;
    if (prevAdded) {
      products = products.concat({
        sku: prevAdded.sku_id,
        original_sku: item.sku_id,
        quantity: 0
      });
    }

    return updateSubstitution({ user, order, status, products })
      .then(() => {
        if (keepQty)
          dispatch({ type: SUBSTITUTION_KEEP, item, quantity: keepQty });
        dispatch({ type: SUBSTITUTION_REMOVE, item, quantity: removeQty });
        return dispatch({
          type: SUBSTITUTION_CART_ADD,
          item,
          newItem,
          quantity
        });
      })
      .catch(res => {
        console.log(res); // eslint-disable-line no-console
        const err = res.body && res.body.message;
        return dispatch({
          type: SUBSTITUTION_REQUEST_ERROR,
          id: item.sku_id,
          error: err
        });
      });
  };
}

export function cancelUpdate(item) {
  return (dispatch, getState) => {
    const state = getState();
    const user = state.login.user;
    const originalOrder = state.substitutions.order;

    const originalProduct = _.find(
      originalOrder.products,
      p => p.sku_id === item.sku_id
    );

    const status = `Reset ${item.title}`;
    dispatch({ type: UPDATE_SUBSTITUTION_STATUS, status });

    dispatch({ type: SUBSTITUTION_REQUEST_LOADING, id: item.sku_id });

    return updateSubstitution({
      user,
      order: originalOrder,
      status,
      products: [
        {
          sku: originalProduct.sku_id,
          original_sku: originalProduct.sku_id,
          quantity: originalProduct.quantity
        }
      ]
    })
      .then(() => {
        return dispatch({ type: RESET_SUBSTITUTION, item });
      })
      .catch(res => {
        console.log(res); // eslint-disable-line no-console
        const err = res.body && res.body.message;
        return dispatch({
          type: SUBSTITUTION_REQUEST_ERROR,
          id: item.sku_id,
          error: err
        });
      });
  };
}

export function confirmUpdate() {
  return (dispatch, getState) => {
    const state = getState();
    const user = state.login.user;
    const order = state.substitutions.order;
    const updates = state.substitutions.updates;

    const products = _.keys(updates).reduce((arr, sku) => {
      const { kept, added } = updates[sku];
      const format = product => ({
        original_sku: sku,
        sku: product.sku_id,
        quantity: product.quantity
      });
      return arr
        .concat(kept ? format(kept) : format({ sku_id: sku, quantity: 0 }))
        .concat(added ? format(added) : []);
    }, []);

    const status = 'Done';

    dispatch({ type: SUBSTITUTION_REQUEST_LOADING, id: 'submit' });

    return updateSubstitution({ user, order, status, products, save: true })
      .then(() => {
        dispatch({ type: SUBSTITUTION_REQUEST_LOADED, id: 'submit' });
        return dispatch({ type: UPDATE_SUBSTITUTION_STATUS, status });
      })
      .catch(res => {
        console.log(res); // eslint-disable-line no-console
        const err = res.body && res.body.message;
        return dispatch({
          type: SUBSTITUTION_REQUEST_ERROR,
          id: 'submit',
          error: err
        });
      });
  };
}
